/* Cliente REST dos documentos legais versionados (ADR-0774). Gestão via `/v1/admin/legal/*` (cookie
   de staff + RBAC no admin-service: READ = 4 tiers, MANAGE = super_admin APENAS).

   Nenhuma função aqui aceita `version`: o número é INTEIRO monotônico calculado NO SERVIDOR
   (`max+1` por docKey). O único lugar em que uma versão entra num body de escrita é
   `copyFromVersion` — que é a ORIGEM a clonar, não a identidade da versão criada.

   Erros passam pelo `mapHttpError`, que preserva o código do backend em `serverCode` — é ele que o
   `legalErrorMessage` usa para distinguir o 409 de IMUTABILIDADE (`legal_version_immutable`) de um
   409 qualquer. Sem esse código a UI só saberia dizer "falha ao salvar". */
import {
  type BulkDeleteLegalDraftsResponse,
  bulkDeleteLegalDraftsResponseDto,
  type CreateLegalVersionBody,
  type LegalDocKey,
  type LegalVersion,
  legalVersionDetailDto,
  legalVersionListDto,
  type UpdateLegalVersionBody,
} from '@planuze/admin-contracts';
import { mapHttpError, parseJson, request } from './http.js';

const BASE = '/v1/admin/legal';

/** GET — histórico completo do docKey (draft + published), uma linha por idioma. */
export async function listLegalVersions(docKey: LegalDocKey): Promise<readonly LegalVersion[]> {
  const res = await request(`${BASE}/documents/${docKey}/versions`);
  if (!res.ok) throw await mapHttpError(res);
  const page = await parseJson(res, legalVersionListDto);
  return page.versions;
}

/** POST — cria a próxima versão (sempre `draft`). O corpo é `locales` XOR `copyFromVersion`. */
export async function createLegalVersion(
  docKey: LegalDocKey,
  body: CreateLegalVersionBody,
): Promise<readonly LegalVersion[]> {
  const res = await request(`${BASE}/documents/${docKey}/versions`, { method: 'POST', body });
  if (!res.ok) throw await mapHttpError(res);
  const created = await parseJson(res, legalVersionListDto);
  return created.versions;
}

/** PUT — edita UMA linha de idioma de versão `draft`. Linha publicada responde 409. */
export async function updateLegalVersion(
  id: string,
  body: UpdateLegalVersionBody,
): Promise<LegalVersion> {
  const res = await request(`${BASE}/versions/${id}`, { method: 'PUT', body });
  if (!res.ok) throw await mapHttpError(res);
  const detail = await parseJson(res, legalVersionDetailDto);
  return detail.version;
}

/** POST publish — publica a VERSÃO inteira (todos os idiomas), não a linha apontada.
    `effectiveAt` ausente = vigora agora; data futura = vigência programada. */
export async function publishLegalVersion(
  id: string,
  effectiveAt?: Date,
): Promise<readonly LegalVersion[]> {
  /* Corpo sempre presente (mesmo vazio): a borda faz `req.json()` e trata ausência de `effectiveAt`
     como "agora" — quem crava o instante é o servidor. */
  const body = effectiveAt === undefined ? {} : { effectiveAt: effectiveAt.toISOString() };
  const res = await request(`${BASE}/versions/${id}/publish`, { method: 'POST', body });
  if (!res.ok) throw await mapHttpError(res);
  const published = await parseJson(res, legalVersionListDto);
  return published.versions;
}

/** DELETE — remove UMA linha de idioma `draft`. Publicada responde 409 (não sai do histórico). */
export async function deleteLegalVersionRow(id: string): Promise<void> {
  const res = await request(`${BASE}/versions/${id}`, { method: 'DELETE' });
  if (!res.ok) throw await mapHttpError(res);
}

/** Remove grupos de versão draft a partir de um id representativo por grupo. */
export async function bulkDeleteLegalDrafts(
  ids: readonly string[],
): Promise<BulkDeleteLegalDraftsResponse> {
  const res = await request(`${BASE}/versions/bulk`, {
    method: 'POST',
    body: { ids, action: 'deleteDrafts' },
  });
  if (!res.ok) throw await mapHttpError(res);
  return parseJson(res, bulkDeleteLegalDraftsResponseDto);
}
